import { useTranslation } from 'react-i18next';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  Tooltip as UITooltip,
} from '@/components/ui/tooltip';
import {
  DealItemWithVersions,
  OfferKpis,
} from '@/lib/graphql/generated/graphql';
import { formatCurrency } from '@/lib/utils';
import { dealItemsStreamingState } from '@/states/products.state';
import { storeAtom } from '@/states/store.state';
import { CompareKpis } from '@/types/compare-offers';
import { Store } from '@/types/store';
import { useAtomValue } from 'jotai';
import { ArrowDown, ArrowUp, HelpCircle, Loader2 } from 'lucide-react';

interface KpisComparisonProps {
  kpis?: CompareKpis | null;
  products: DealItemWithVersions[];
}

interface KpiCardProps {
  title: string;
  currentValue: number | null;
  previousValue: number | null;
  unit: string;
  type: 'percentage' | 'currency' | 'integer';
  tooltip?: string;
  reverseTrend?: boolean;
  customContent?: React.ReactNode;
  isStreaming?: boolean;
}

const SEUIL = 10000;

const numberFR = (n: number, opts?: Intl.NumberFormatOptions) =>
  new Intl.NumberFormat('fr-FR', opts).format(n);

// Affiche "xx K" avec séparateur FR, et décimales paramétrables
const formatCompactK = (displayNum: number, decimals = 0) => {
  const sign = displayNum < 0 ? '-' : '';
  const n = Math.abs(displayNum) / 1000;
  return `${sign}${numberFR(n, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })} k`;
};

const formatPercentage = (fraction: number) => {
  const percent = fraction * 100;
  if (Math.abs(percent) > SEUIL) {
    return formatCompactK(percent, 0);
  }
  return numberFR(percent, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
};

const formatInteger = (value: number) => {
  if (Math.abs(value) > SEUIL) {
    return formatCompactK(value, 0);
  }
  return numberFR(Math.round(value));
};

const formatValue = (
  value: number | null,
  type: 'percentage' | 'currency' | 'integer' | string,
  store: Store
) => {
  if (value === null) return 'N/A';
  if (type === 'percentage') return formatPercentage(value);
  if (type === 'currency') {
    const valueInUnits = value / 100;
    if (Math.abs(valueInUnits) > SEUIL) {
      return formatCompactK(valueInUnits, 1);
    }
    return formatCurrency(valueInUnits, store);
  }
  if (type === 'integer') return formatInteger(value);
  return value.toString(1);
};

// Helper function to calculate bestsellers count from products
const calculateBestsellers = (products: DealItemWithVersions[]) => {
  return products.filter((p) => p.activeVersion.isBestSeller).length;
};

// Helper functions to calculate current KPIs from products
const calculateCurrentKpis = (products: DealItemWithVersions[]): OfferKpis => {
  const selectedProducts = products.filter((p) => p.activeVersion.isSelected);
  const productsWithPrice = selectedProducts.filter(
    (p) =>
      p.activeVersion.choosePrice !== undefined &&
      p.activeVersion.choosePrice !== null
  );

  // Calculate average discount
  const totalDiscount = productsWithPrice.reduce(
    (acc, p) => acc + p.activeVersion.chooseDiscount!,
    0
  );
  const avgDiscount =
    productsWithPrice.length > 0 ? totalDiscount / productsWithPrice.length : 0;

  // Calculate average choose price
  const totalChoosePrice = productsWithPrice.reduce(
    (acc, p) => acc + p.activeVersion.choosePrice!,
    0
  );

  const avgChoosePrice =
    productsWithPrice.length > 0
      ? totalChoosePrice / productsWithPrice.length
      : 0;

  // Calculate average margin
  const productsWithMargin = selectedProducts.filter(
    (p) =>
      p.activeVersion.marginPercentage !== undefined &&
      p.activeVersion.marginPercentage !== null
  );
  const totalMargin = productsWithMargin.reduce(
    (acc, p) => acc + p.activeVersion.marginPercentage!,
    0
  );
  const avgMargin =
    productsWithMargin.length > 0 ? totalMargin / productsWithMargin.length : 0;

  // Calculate total potential
  const totalPotential = selectedProducts.reduce((acc, p) => {
    const stock = p.activeVersion.stockQuantity || 0;
    const price = p.activeVersion.choosePrice || 0;
    return acc + stock * price * 100; // Convert to cents
  }, 0);

  // Calculate total stock
  const totalStock = selectedProducts.reduce(
    (acc, p) => acc + (p.activeVersion.stockQuantity || 0),
    0
  );

  return {
    avgDiscount: avgDiscount / 100, // Convert to decimal
    avgChoosePrice: avgChoosePrice * 100, // Convert to cents
    avgMargin: avgMargin / 100, // Convert to decimal
    totalPotential,
    productsCount: selectedProducts.length,
    totalStock,
  };
};

function KpiCard({
  title,
  currentValue,
  previousValue,
  unit,
  type,
  tooltip,
  reverseTrend = false,
  customContent,
  isStreaming = false,
}: KpiCardProps) {
  const store = useAtomValue(storeAtom);
  let difference = null;
  let percentChange = null;
  let isPositive = null;
  if (typeof currentValue === 'number' && typeof previousValue === 'number') {
    difference = currentValue - previousValue;
    percentChange =
      previousValue !== 0 ? (difference / previousValue) * 100 : 0;
    isPositive = reverseTrend ? difference < 0 : difference > 0;
  }

  const differenceLabel =
    difference !== null ? formatValue(difference, type, store) : '';
  const differenceUnitLabel = type === 'percentage' ? ' pts' : unit;
  const isMediumPrice = title.toLowerCase().includes('prix moyen choose');
  const isGreen = isMediumPrice ? !isPositive : isPositive;
  return (
    <Card className="shadow-sm">
      <CardHeader className="px-4 py-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium text-gray-500">
            {title}
          </CardTitle>
          {tooltip && (
            <TooltipProvider>
              <UITooltip>
                <TooltipTrigger>
                  <HelpCircle className="h-4 w-4 text-gray-400" />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-xs">{tooltip}</p>
                </TooltipContent>
              </UITooltip>
            </TooltipProvider>
          )}
        </div>
      </CardHeader>
      <CardContent className="px-4 pb-2 pt-0">
        {isStreaming ? (
          <div className="flex h-7 items-center">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : customContent ? (
          customContent
        ) : (
          <>
            <div className="text-xl font-bold">
              {formatValue(currentValue, type, store)}
              {unit}
              {differenceLabel && (
                <>
                  {' '}
                  vs {formatValue(previousValue, type, store)}
                  {unit}
                </>
              )}
            </div>
            {differenceLabel && (
              <div className="mt-1 flex items-center">
                <div
                  className={`mr-1 text-xs ${isGreen ? 'text-green-600' : 'text-red-600'}`}
                >
                  {isPositive ? (
                    <ArrowUp className="inline h-3 w-3" />
                  ) : (
                    <ArrowDown className="inline h-3 w-3" />
                  )}
                </div>
                <div
                  className={`text-xs ${isGreen ? 'text-green-600' : 'text-red-600'}`}
                >
                  {differenceLabel}
                  {differenceUnitLabel}
                  {percentChange ? ` (${percentChange.toFixed(1)} %)` : ''}
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

export function KpisComparison({ kpis, products }: KpisComparisonProps) {
  const { t } = useTranslation();
  const isStreaming = useAtomValue(dealItemsStreamingState);
  const isComparisonMode = !!kpis;
  // Calculate current KPIs - use atom for average discount when available, otherwise calculate from products
  const currentKpisCalculated = calculateCurrentKpis(products);
  const currentKpis: OfferKpis = isComparisonMode
    ? kpis.currentDealKpis
    : {
        ...currentKpisCalculated,
        avgDiscount: currentKpisCalculated.avgDiscount,
        avgMargin: currentKpisCalculated.avgMargin,
      };
  const previousKpis: OfferKpis | null = isComparisonMode
    ? kpis.previousCampaignKpis
    : null;

  return (
    <div className="space-y-4">
      {!isComparisonMode && (
        <h3 className="text-lg font-medium">
          {t('general.current_offer_analysis')}
        </h3>
      )}

      {/* Main KPIs */}
      <div className="flex flex-col gap-3">
        <KpiCard
          title={t('components.kpi_comparison.average_discount')}
          currentValue={currentKpis.avgDiscount ?? null}
          previousValue={previousKpis?.avgDiscount ?? null}
          type="percentage"
          unit="%"
          tooltip={t('components.kpi_comparison.average_discount_tooltip')}
          isStreaming={isStreaming}
        />

        <KpiCard
          title={t('components.kpi_comparison.average_margin')}
          currentValue={currentKpis.avgMargin ?? null}
          previousValue={previousKpis?.avgMargin ?? null}
          type="percentage"
          unit="%"
          tooltip={t('components.kpi_comparison.average_margin_tooltip')}
          isStreaming={isStreaming}
        />

        <KpiCard
          title={t('components.kpi_comparison.average_choose_price')}
          currentValue={currentKpis.avgChoosePrice ?? null}
          previousValue={previousKpis?.avgChoosePrice ?? null}
          type="currency"
          unit=""
          tooltip={t('components.kpi_comparison.average_choose_price_tooltip')}
          isStreaming={isStreaming}
        />

        <KpiCard
          title={t('components.kpi_comparison.total_potential')}
          currentValue={currentKpis.totalPotential ?? null}
          previousValue={previousKpis?.totalPotential ?? null}
          type="currency"
          unit=""
          tooltip={t('components.kpi_comparison.total_potential_tooltip')}
          isStreaming={isStreaming}
        />
      </div>

      {/* Secondary KPIs */}
      <div className="flex flex-col gap-3">
        <KpiCard
          title={t('components.kpi_comparison.in_offer_variants')}
          currentValue={currentKpis.productsCount}
          previousValue={previousKpis?.productsCount ?? null}
          unit=""
          type="integer"
          tooltip={t('components.kpi_comparison.in_offer_variants_tooltip')}
          isStreaming={isStreaming}
        />

        <KpiCard
          title={t('components.kpi_comparison.total_stock')}
          currentValue={currentKpis.totalStock}
          previousValue={previousKpis?.totalStock ?? null}
          type="integer"
          unit=""
          tooltip={t('components.kpi_comparison.total_stock_tooltip')}
          isStreaming={isStreaming}
        />
      </div>

      {/* Additional KPIs - Current offer only (no comparison available) */}
      {products.length > 0 && (
        <div className="flex flex-col gap-3">
          <Card className="shadow-sm">
            <CardHeader className="px-4 py-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-gray-500">
                  {t('components.kpi_comparison.top_sales_choose')}
                </CardTitle>
                <TooltipProvider>
                  <UITooltip>
                    <TooltipTrigger>
                      <HelpCircle className="h-3 w-3 text-gray-400" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="text-xs">
                        {t(
                          'components.kpi_comparison.top_sales_choose_tooltip'
                        )}
                      </p>
                    </TooltipContent>
                  </UITooltip>
                </TooltipProvider>
              </div>
            </CardHeader>
            <CardContent className="flex flex-col justify-between px-4 py-2">
              {isStreaming ? (
                <div className="flex h-7 items-center">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <>
                  <div>
                    <div className="text-xl font-bold">
                      {calculateBestsellers(products)}
                    </div>
                    <div className="mt-1 text-xs text-gray-500">
                      {t('components.kpi_comparison.current_data_only')}
                    </div>
                  </div>
                  <div className="mt-2">
                    <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100">
                      <div
                        className="h-full rounded-full bg-yellow-400"
                        style={{
                          width: `${(calculateBestsellers(products) / products.length) * 100}%`,
                        }}
                      />
                    </div>
                    <div className="mt-1 text-xs text-gray-600">
                      {(
                        (calculateBestsellers(products) / products.length) *
                        100
                      ).toFixed(1)}
                      {t('components.kpi_comparison.of_catalog')}
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
